import { SKETCH_TOOLS } from "@/lib/sketch-core/registry";
import { Wrench, Zap, Wind, HardHat, Droplets, ClipboardList, type LucideIcon } from "lucide-react";
import type { CatalogGroupId } from "@/lib/material-catalog";

/**
 * The Trade Hub — one row per trade, always the same three doors:
 *   Calculators (trade page) · Catalog (Shop prices) · Labor (Shop rates)
 *
 * Adding a trade here never adds a nav slot; it adds a row on /trades.
 */
export interface TradeHubEntry {
  id: string;
  label: string;
  desc: string;
  icon: LucideIcon;
  /** Trade page with the calculators and reference tables */
  calcPath: string;
  calcLabel: string;
  /** Catalog sections in the Shop that belong to this trade */
  groups: CatalogGroupId[];
  /** Which labor rate table this trade uses */
  labor: "pipe" | "electrical" | "structural" | "hvac";
  /** Deep link to this trade's sketch pad, when it has one */
  sketchPath?: string;
  sketchLabel?: string;
}

const TRADE_LIST: TradeHubEntry[] = [
  {
    id: "pipe",
    label: "Pipe & Mechanical",
    desc: "Sizing, offsets, steam, gas, hangers & isometric sketching",
    icon: Wrench,
    calcPath: "/pipe",
    calcLabel: "Pipe calculators",
    groups: ["pipe", "fittings", "valves", "supports", "insulation"],
    labor: "pipe",
  },
  {
    id: "plumbing",
    label: "Plumbing",
    desc: "Fixtures, traps, drainage, water pipe & valves",
    icon: Droplets,
    calcPath: "/pipe?tool=fixtures",
    calcLabel: "Plumbing calculators",
    groups: ["plumbing", "valves", "supports"],
    labor: "pipe",
  },
  {
    id: "electrical",
    label: "Electrical",
    desc: "Wire, conduit fill, panels, gear, motors & voltage drop",
    icon: Zap,
    calcPath: "/electrical",
    calcLabel: "Electrical calculators",
    groups: ["conduit", "wire", "devices"],
    labor: "electrical",
  },
  {
    id: "hvac",
    label: "HVAC & Ductwork",
    desc: "Duct sizing, airflow, psychrometrics, coils & registers",
    icon: Wind,
    calcPath: "/ductwork",
    calcLabel: "Ductwork calculators",
    groups: ["hvac", "insulation", "supports"],
    labor: "hvac",
  },
  {
    id: "structural",
    label: "Structural",
    desc: "Beams, anchors, seismic bracing, concrete, rebar & steel",
    icon: HardHat,
    calcPath: "/structural",
    calcLabel: "Structural calculators",
    groups: ["structural", "supports"],
    labor: "structural",
  },
  {
    id: "general",
    label: "General job costs",
    desc: "Permits, rentals, dumpsters, consumables, travel & per diem",
    icon: ClipboardList,
    calcPath: "/jobs?tool=estimate",
    calcLabel: "Job estimate",
    groups: ["general"],
    labor: "pipe",
  },
];

/**
 * Sketch pads come from the one sketch registry, so adding a pad lights it up
 * on the Trades page automatically.
 */
export const TRADE_HUB: TradeHubEntry[] = TRADE_LIST.map((t) => {
  const pad = SKETCH_TOOLS.find((s) => s.trade === t.id);
  return pad ? { ...t, sketchPath: pad.route, sketchLabel: pad.label } : t;
});

/** Deep link into the Shop catalog filtered to one trade's sections. */
export const catalogLink = (t: TradeHubEntry) =>
  `/shop?tool=materials&groups=${t.groups.join(",")}`;

/** Deep link into the Shop labor rates for this trade. */
export const laborLink = (t: TradeHubEntry) => `/shop?tool=labor&trade=${t.labor}`;
